import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import './style.scss';
import Layout from 'components/Layout/Layout';
import Table from 'components/common/Table';
import api from 'services/api';
import ClassifierCard from 'components/Requester/ClassifierCard';
import RequestHeaders from 'components/Requester/RequestHeaders';

const Logs = () => {
  const currentLogger = useSelector((state) => state.global.currentLogger);
  let since = new Date().toISOString();

  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const [curPage, setCurPage] = useState(1);
  const [nRequests] = useState(50);
  const [hasMore, setHasMore] = useState(true);

  const [botFilter, setBotFilter] = useState('');
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const formatTimestamp = (timestamp) => {
    if (!timestamp) {
      return;
    }

    return new Date(timestamp).toLocaleString();
  };

  const parseData = (dataToParse) => {
    console.log('data to parse:', dataToParse);
    if (!dataToParse.requests) {
      return;
    }
    const logs = dataToParse.requests;
    const dataParsed = [];
    for (let i = 0; i < logs.length; i++) {
      dataParsed.push([
        logs[i],
        logs[i].method,
        logs[i].uri,
        {
          ipAddress: logs[i].ipAddress,
          loggerId: currentLogger,
          requesterId: logs[i].requesterId,
        },
        formatTimestamp(logs[i].createdAt),
        logs[i].timeTaken,
        logs[i].country,
        logs[i].groupName,
        logs[i].action,
      ]);
    }
    return dataParsed;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    return String(value);
  };

  const renderExpandedRow = (row) => {
    const details = [
      ['Username', row.username],
      ['Session ID', row.sessionId],
      ['Referer', row.referer],
      ['Number of Queries', row.numberQueries],
      ['User Agent', row.userAgent, true],
      ['Bot', row.ztr_bot],
      ['Bot Category', row.ztr_bot_cat],
      ['Status Code', row.statusCode],
      ['AS Number', row.ztr_asnum],
    ];

    return (
      <div className="columns is-multiline">
        {details.map(([label, value, full]) => (
          <div className={`column ${full ? 'is-full' : 'is-half'}`} key={label}>
            <strong>
              {label}
              :
            </strong>
            {' '}
            {formatValue(value)}
          </div>
        ))}
        <RequestHeaders headers={row.headers} />
        <div className="column is-half">
          <ClassifierCard request={row} />
        </div>
      </div>
    );
  };

  /**
   * Fetches for log data.
   */
  const fetchLogs = useCallback((curPage = 1) => {
    setIsError(false);
    api.getLogs(
      currentLogger,
      nRequests,
      curPage,
      since,
      sortColumn,
      sortOrder,
      botFilter,
    (res) => {
      setIsLoading(false);
      if (res.data.status === 200) {
        if (res.data.requests.length < nRequests) {
          setHasMore(false);
        }
        if (curPage !== 1) {
          setData(previousData => ({
            status: res.data.status,
            requests: [...previousData.requests, ...res.data.requests],
          }));
        } else {
          if (res.data.requests.length > 0) {
            const firstRequestCreatedAt = res.data.requests[0].createdAt;
            if (new Date(firstRequestCreatedAt) < new Date(since)) {
              since = firstRequestCreatedAt;
            }
          }
          setData(res.data);
        }
      } else {
        setIsError(true);
      }
    });
  }, [currentLogger, nRequests, sortColumn, sortOrder, botFilter]);

  const incrementPage = () => {
    const nextPage = curPage + 1;
    setCurPage(nextPage);
    fetchLogs(nextPage);
  };

  const handleSort = (column) => {
    setSortOrder(previousOrder =>
      column === sortColumn && previousOrder === 'desc' ? 'asc' : 'desc'
    );
    setSortColumn(column);
    setCurPage(1);
    setHasMore(true);
  };

  /**
   * Calls fetchLogs onload.
   */
  useEffect(() => {
    setIsLoading(true);
    fetchLogs();
  }, [fetchLogs]);

  return (
    <Layout title={'Logs'} loading={isLoading} error={isError}>
      {isError}
      <div className="filter-row">
        <div className="field">
          <label className="label" htmlFor="bot-filter">Bot</label>
          <div className="select is-small">
            <select id="bot-filter" onChange={(e) => setBotFilter(e.target.value)} value={botFilter}>
              <option value="">All</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </div>
        </div>
      </div>
      <Table
        headerAttributes={[
          'Method', 'URI', 'IP Address', 'Timestamp',
          'Processing Time', 'Country', 'Group',
          'Action'
        ]}
        drawMethodPosition={1}
        drawURIPosition={2}
        drawIpAddressPosition={3}
        data={parseData(data)}
        callBackUrl={incrementPage}
        hasMore={hasMore}
        sortableColumns={
          ['method', 'uri', 'ipAddress', 'createdAt',
            'timeTaken', 'country', 'groupName', 'action']
        }
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onSort={handleSort}
        renderExpandedRow={renderExpandedRow}
      />
    </Layout>
  );
};

export default Logs;
